import { DatePipe } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";
import { IApiResponse } from "@models/api-response.model";
import { VendorProductSyncComponent } from "@pages/vendor/vendor-detail/vendor-product-sync.component";
import { RequestService } from "@services/index";
import { TelegramIngestPanelComponent } from "./telegram-ingest-panel.component";

/** What's holding this vendor's sync lock right now, from any admin-backend instance. */
interface ActiveSyncLock {
  vendorName: string;
  collection: string;
  host: string;
  isThisInstance: boolean;
  startedAt: string;
  cancelRequested: boolean;
  done: number;
  total: number;
  currentTitle: string;
}

interface SyncVendor {
  key: string;
  /** "page-map" vendors sync collections; "telegram" ones ingest posts and confirm. */
  kind: "page-map" | "telegram";
  sourceName: string;
  /** ISO-2 codes of the markets this vendor's products are listed in. */
  markets: string[];
  vendorId: number;
  vendorName: string;
  buttons: number;
  totalProducts: number;
  inStock: number;
  activeLock: ActiveSyncLock | null;
  /** The oldest "last updated" among this vendor's collections — its most overdue sync. */
  lastUpdated: string | null;
}

/**
 * Every vendor's Product Sync on one page, one collapsible section per vendor — so the
 * page reads as a list of vendors first, and opening one reveals its buttons. Each
 * section's content is the same panel as the vendor page's Product Sync tab, so a sync
 * started or cancelled here shows its progress there too, and the other way round: both
 * read the backend's shared job list. The vendor list comes from the backend's provider
 * registry, so a newly added sync appears on its own.
 *
 * A collapsed section's panel is not rendered at all (matExpansionPanelContent defers
 * it), so an unopened vendor never starts polling its sync status — only the ones a
 * reader actually expands do.
 */
@Component({
  selector: "fh-product-sync-list",
  templateUrl: "./product-sync-list.component.html",
  styleUrls: ["./product-sync-list.component.scss"],
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatTooltipModule,
    RouterLink,
    TelegramIngestPanelComponent,
    VendorProductSyncComponent,
  ],
})
export class ProductSyncListComponent implements OnInit {
  vendors = signal<SyncVendor[]>([]);
  loading = signal<boolean>(true);
  error = signal<string>("");

  constructor(private requestService: RequestService) {}

  ngOnInit() {
    this.requestService.get<IApiResponse>("/api/v2/product-sync/providers").subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res.status || !res.data) {
          this.error.set(res.message || "Could not load product syncs");
          return;
        }
        const list = (res.data as { providers: SyncVendor[] }).providers;
        this.vendors.set(
          list
            // Defensive: an older backend does not send markets, and the header
            // iterates it. An empty list simply shows no chip.
            .map((v) => ({ ...v, markets: v.markets ?? [] }))
            .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
        );
      },
      error: () => {
        this.loading.set(false);
        this.error.set("Could not load product syncs");
      },
    });
  }

  /** Stops the "Open vendor" link's click from also toggling the panel it sits inside. */
  stopToggle(event: Event) {
    event.stopPropagation();
  }

  /** 0–100 for a vendor's active sync, so the collapsed header can show a progress bar. */
  progress(v: SyncVendor): number {
    const lock = v.activeLock;
    if (!lock || !lock.total) return 0;
    return Math.round((lock.done / lock.total) * 100);
  }
}
